import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Button, Space, Tabs, Popconfirm, message, Badge } from 'antd';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import PageHeader from '../../../shared/components/PageHeader';
import transferService, { TRANSFER_TYPE_LABELS } from '../../services/transferService';
import NewTransfer from './NewTransfer';

const STATUS = {
    pending: ['processing', 'Pending acceptance'],
    accepted: ['success', 'Completed'],
    declined: ['error', 'Rejected'],
    cancelled: ['default', 'Canceled'],
    expired: ['default', 'Expired'],
};

/** 资源转移：转入 / 转出请求，接受 · 拒绝 · 取消。 */
const Transfers = () => {
    const { t } = useTranslation();
    const [tab, setTab] = useState('in');
    const [items, setItems] = useState([]);
    const [pendingIn, setPendingIn] = useState(0);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);
    const [params, setParams] = useSearchParams();
    const creating = params.get('new') === '1';
    const init = creating ? { type: params.get('type'), id: params.get('id') } : null;
    const closeNew = () => setParams({}, { replace: true });
    const created = () => {
        closeNew();
        setTab('out');
        window.dispatchEvent(new Event('transfers:changed'));
        load();
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await transferService.list();
            setItems(Array.isArray(d?.items) ? d.items : []);
            setPendingIn(d?.pending_in || 0);
        } catch (e) {
            message.error(t(e.error || 'Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const act = async (fn, id, ok) => {
        setBusy(id);
        try {
            await fn(id);
            message.success(t(ok));
            window.dispatchEvent(new Event('transfers:changed'));
        } catch (e) {
            message.error(t(e.error || 'Operation failed'));
        } finally {
            setBusy(null);
            load();
        }
    };

    const rows = items.filter((r) => r.direction === tab);
    const columns = [
        {
            title: t('Resources'), key: 'res',
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <span>{r.resource_name}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{t(TRANSFER_TYPE_LABELS[r.resource_type] || r.resource_type)}</span>
                </Space>
            ),
        },
        { title: tab === 'in' ? t('Transfer from') : t('Transfer to'), dataIndex: 'counterpart', key: 'who' },
        {
            title: t('Status'), dataIndex: 'status', key: 'status',
            render: (s) => <Tag color={(STATUS[s] || [])[0]}>{t((STATUS[s] || [null, s])[1])}</Tag>,
        },
        {
            title: t('Time'), key: 'time',
            render: (_, r) => (r.status === 'pending' ? t('Expires {{date}}', { date: r.expires_at }) : (r.decided_at || r.created_at)),
        },
        {
            title: '', key: 'actions', align: 'right',
            render: (_, r) => (r.status !== 'pending' ? null : tab === 'in' ? (
                <Space>
                    <Popconfirm title={t('Once accepted, you pay the renewals')} onConfirm={() => act(transferService.accept, r.id, 'Accepted')}>
                        <Button type="primary" size="small" loading={busy === r.id}>{t('Accept')}</Button>
                    </Popconfirm>
                    <Popconfirm title={t('Decline this transfer?')} onConfirm={() => act(transferService.decline, r.id, 'Rejected')}>
                        <Button size="small" disabled={busy === r.id}>{t('Deny')}</Button>
                    </Popconfirm>
                </Space>
            ) : (
                <Popconfirm title={t('Cancel this transfer?')} onConfirm={() => act(transferService.cancel, r.id, 'Canceled')}>
                    <Button size="small" loading={busy === r.id}>{t('Cancel')}</Button>
                </Popconfirm>
            )),
        },
    ];

    return (
        <div>
            <PageHeader title={t('Resource transfer')} extra={(
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setParams({ new: '1' })}>{t('New transfer')}</Button>
                </Space>
            )} />
            <Card>
                <Tabs activeKey={tab} onChange={setTab} items={[
                    { key: 'in', label: <Badge count={pendingIn} size="small" offset={[8, 0]}>{t('Transfer in')}</Badge> },
                    { key: 'out', label: t('Transfer out') },
                ]} />
                <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 20, hideOnSinglePage: true }}
                    scroll={{ x: 640 }} locale={{ emptyText: tab === 'in' ? t('No incoming transfer requests') : t('No outgoing transfer requests') }} />
            </Card>
            <NewTransfer open={creating} init={init} onClose={closeNew} onDone={created} />
        </div>
    );
};

export default Transfers;
